/**
 * Small pure helpers shared by every place that needs to reason about
 * markdown source outside of the full `MarkdownView` renderer: single-line
 * `truncate`/`line-clamp` contexts (table rows, tiles) that must never leak
 * raw markdown syntax (`![alt](src)`, `[text](href)`, `**bold**`, …) to the
 * user, and tile toggles that need to know whether a description embeds an
 * image before deciding whether to show a "see more" control.
 */

const IMAGE_PATTERN = /!\[[^\]]*\]\([^)]*\)/g
const LINK_PATTERN = /\[([^\]]*)\]\([^)]*\)/g
const MARKDOWN_MARKER_PATTERN = /[*_~#>]+/g

/** Whether the markdown source embeds at least one `![alt](src)` image. */
export function markdownContainsImage(markdown: string): boolean {
  return /!\[[^\]]*\]\([^)]*\)/.test(markdown)
}

/**
 * Strips markdown syntax down to a plain-text excerpt: images are removed
 * entirely, links keep only their visible text, and emphasis/heading/quote
 * markers (`*_~#>`) are dropped. Optionally truncates to `maxLength`
 * characters (on a full excerpt, never mid-URL) with an ellipsis.
 */
export function markdownToPlainExcerpt(markdown: string, maxLength = Infinity): string {
  const plain = markdown
    .replace(IMAGE_PATTERN, '')
    .replace(LINK_PATTERN, '$1')
    .replace(MARKDOWN_MARKER_PATTERN, '')
    .replace(/\s+/g, ' ')
    .trim()

  if (plain.length <= maxLength) return plain
  return `${plain.slice(0, maxLength).trimEnd()}…`
}
